import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { restaurantApi } from '../services/api';
import { Restaurant, AddressDto } from '../types';

interface MerchantContextType {
  merchantId: string | null;
  restaurant: Restaurant | null;
  isLoading: boolean;
  error: Error | null;
  deliveryOption: 'delivery' | 'takeaway';
  setDeliveryOption: (option: 'delivery' | 'takeaway') => void;
  scheduledDate: string | null;
  setScheduledDate: (date: string | null) => void;
  scheduledTime: string | null;
  setScheduledTime: (time: string | null) => void;
  selectedAddress: AddressDto | null;
  setSelectedAddress: (address: AddressDto | null) => void;
  shouldRecalculateFee: boolean;
  setShouldRecalculateFee: (should: boolean) => void;
  showAddressModal: boolean;
  setShowAddressModal: (show: boolean) => void;
  isCouponModalOpen: boolean;
  setIsCouponModalOpen: (show: boolean) => void;
  isCheckoutModalOpen: boolean;
  setIsCheckoutModalOpen: (show: boolean) => void;
  isPreCheckoutSuggestionsModalOpen: boolean;
  setIsPreCheckoutSuggestionsModalOpen: (show: boolean) => void;
  hasSeenSuggestions: boolean;
  setHasSeenSuggestions: (hasSeen: boolean) => void;
  selectedOrder: any | null;
  setSelectedOrder: (order: any | null) => void;
  productModalData: { item: any; editMode: boolean; editIndex?: number } | null;
  setProductModalData: (data: { item: any; editMode: boolean; editIndex?: number } | null) => void;
  deliveryFeeInfo: { fee: number; time: string } | null;
  setDeliveryFeeInfo: (info: { fee: number; time: string } | null) => void;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  menuCategories: any[];
  setMenuCategories: (categories: any[]) => void;
  refreshAvailability: () => Promise<boolean>;
  enabledFeatures: string[];
  hasFeature: (feature: string) => boolean;
}

const MerchantContext = createContext<MerchantContextType | undefined>(undefined);

export const MerchantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [merchantId, setMerchantId] = useState<string | null>(localStorage.getItem('merchantId'));
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Address states
  const [deliveryOption, setDeliveryOptionState] = useState<'delivery' | 'takeaway'>(
    (localStorage.getItem('deliveryOption') as 'delivery' | 'takeaway') || 'delivery'
  );
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);

  const [selectedAddress, setSelectedAddressState] = useState<AddressDto | null>(
    localStorage.getItem('selectedAddress') ? JSON.parse(localStorage.getItem('selectedAddress')!) : null
  );
  const [shouldRecalculateFee, setShouldRecalculateFee] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Coupon Modal state
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);

  // Checkout Modal state
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isPreCheckoutSuggestionsModalOpen, setIsPreCheckoutSuggestionsModalOpen] = useState(false);
  const [hasSeenSuggestions, setHasSeenSuggestions] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);

  // Product Modal state
  const [productModalData, setProductModalData] = useState<{ item: any; editMode: boolean; editIndex?: number } | null>(null);

  // Delivery Fee state
  const [deliveryFeeInfo, setDeliveryFeeInfo] = useState<{ fee: number; time: string } | null>(null);

  // Cart Modal state (mobile)
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Menu Categories state for suggestions and other global uses
  const [menuCategories, setMenuCategories] = useState<any[]>([]);

  const refreshAvailability = useCallback(async (): Promise<boolean> => {
    if (!merchantId) return false;
    try {
      const isAvailable = await restaurantApi.isAvailable(merchantId);
      setRestaurant(prev => {
        if (prev && prev.isOpen !== isAvailable) {
          return { ...prev, isOpen: isAvailable };
        }
        return prev;
      });
      return isAvailable;
    } catch (e) {
      console.error('Error refreshing availability:', e);
      return restaurant?.isOpen ?? false;
    }
  }, [merchantId, restaurant?.isOpen]);

  const setDeliveryOption = (option: 'delivery' | 'takeaway') => {
    setDeliveryOptionState(option);
    localStorage.setItem('deliveryOption', option);
  };

  const setSelectedAddress = (address: AddressDto | null) => {
    setSelectedAddressState(address);
    if (address) {
      localStorage.setItem('selectedAddress', JSON.stringify(address));
      setShouldRecalculateFee(true);
    } else {
      localStorage.removeItem('selectedAddress');
    }
  };

  const fetchRestaurantData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      let currentMerchantId = merchantId;
      
      if (!currentMerchantId) {
        const domain = window.location.hostname;
        const pathname = window.location.pathname;
        const pathParts = pathname.split('/').filter(Boolean);
        const slug = pathParts[0];

        // List of routes that are NOT slugs
        const reservedPaths = [
          'home', 'menu', 'promocoes', 'pedidos', 'perfil', 'cashback', 
          'login', 'registrar', 'table-menu', 'counter-menu', 
          'schedule-order', 'order-success', 'avaliar'
        ];

        try {
          if ((domain === 'app.leodelivery.com.br' || domain === 'localhost' || domain === '127.0.0.1') && slug && !reservedPaths.includes(slug)) {
            const response = await restaurantApi.getMerchantBySlug(slug);
            currentMerchantId = response.merchantId;
          } else {
            const response = await restaurantApi.getMerchantByDomain(domain);
            currentMerchantId = response.merchantId;
          }
        } catch (e) {
          console.error('Error identifying merchant:', e);
          // Fallback to domain lookup as a last resort
          try {
            const response = await restaurantApi.getMerchantByDomain(domain);
            currentMerchantId = response.merchantId;
          } catch (fallbackErr) {
            console.error('Fallback domain lookup also failed:', fallbackErr);
          }
        }

        if (currentMerchantId) {
          setMerchantId(currentMerchantId);
          localStorage.setItem('merchantId', currentMerchantId);
        }
      }

      if (currentMerchantId) {
        try {
          // Fetch main restaurant data and all additional data in parallel
          const [restaurantData, banners, openingHours, paymentMethods, settings, theme, cashbackConfig, isAvailable] = await Promise.all([
            restaurantApi.getRestaurant(currentMerchantId),
            restaurantApi.getBanners(currentMerchantId).catch(e => { console.error("Error fetching banners", e); return []; }),
            restaurantApi.getOpeningHours(currentMerchantId).catch(e => { console.error("Error fetching opening hours", e); return []; }),
            restaurantApi.getPaymentMethods(currentMerchantId).catch(e => { console.error("Error fetching payment methods", e); return []; }),
            restaurantApi.getSettings(currentMerchantId).catch(e => { console.error("Error fetching settings", e); return null; }),
            restaurantApi.getTheme(currentMerchantId).catch(e => { console.error("Error fetching theme", e); return null; }),
            restaurantApi.getCashbackConfig(currentMerchantId).catch(e => { console.error("Error fetching cashback config", e); return null; }),
            restaurantApi.isAvailable(currentMerchantId).catch(e => { console.error("Error fetching availability", e); return null; })
          ]);
          
          // Construct the full updated restaurant object
          const updated: Restaurant = {
            ...restaurantData,
            isOpen: isAvailable !== null ? isAvailable : restaurantData.isOpen,
            banners: banners.length > 0 ? banners : (restaurantData.banners || []),
            openingHours: openingHours.length > 0 ? openingHours : (restaurantData.openingHours || []),
            paymentMethods: paymentMethods.length > 0 ? paymentMethods : (restaurantData.paymentMethods || []),
            deliveryOptions: settings ? settings : restaurantData.deliveryOptions,
            theme: theme ? theme : restaurantData.theme,
            cashbackConfig: cashbackConfig || restaurantData.cashbackConfig
          };

          // Update restaurant with all information at once, with deep comparison check
          setRestaurant(prev => {
            if (!prev) return updated;
            
            // Fast comparison using stringify - could be improved with a proper deepEqual
            // but for DTOs this is usually sufficient and prevents unnecessary re-renders.
            if (JSON.stringify(prev) === JSON.stringify(updated)) {
              return prev;
            }
            return updated;
          });
        } catch (resErr) {
          console.error('Error fetching restaurant detail:', resErr);
          // If we have merchantId but failed to get restaurant details, it's an error
          setError(resErr instanceof Error ? resErr : new Error('Failed to fetch restaurant details'));
        }
      } else {
        setError(new Error('Merchant not found for this domain'));
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching merchant data:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch merchant data'));
      setIsLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchRestaurantData();
  }, [fetchRestaurantData]);

  // Polling for restaurant info (open/closed status)
  useEffect(() => {
    if (!merchantId) return;

    const interval = setInterval(() => {
      fetchRestaurantData(true);
      refreshAvailability();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [merchantId, fetchRestaurantData]);

  return (
    <MerchantContext.Provider value={{ 
      merchantId, 
      restaurant, 
      isLoading, 
      error, 
      deliveryOption, 
      setDeliveryOption, 
      scheduledDate,
      setScheduledDate,
      scheduledTime,
      setScheduledTime,
      selectedAddress, 
      setSelectedAddress, 
      shouldRecalculateFee,
      setShouldRecalculateFee,
      showAddressModal, 
      setShowAddressModal,
      isCouponModalOpen,
      setIsCouponModalOpen,
      isCheckoutModalOpen,
      setIsCheckoutModalOpen,
      isPreCheckoutSuggestionsModalOpen,
      setIsPreCheckoutSuggestionsModalOpen,
      hasSeenSuggestions,
      setHasSeenSuggestions,
      selectedOrder,
      setSelectedOrder,
      productModalData,
      setProductModalData,
      deliveryFeeInfo,
      setDeliveryFeeInfo,
      isCartOpen,
      setIsCartOpen,
      menuCategories,
      setMenuCategories,
      refreshAvailability,
      enabledFeatures: restaurant?.enabledFeatures || [],
      hasFeature: (feature: string) => {
        const features = restaurant?.enabledFeatures || [];
        return features.includes(feature.trim().toUpperCase());
      }
    }}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = () => {
  const context = useContext(MerchantContext);
  if (context === undefined) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
};
